/** GRAPHICAL_ASSETS control: upload one source image → the backend generates the app icon / Explore
 *  banner / splash to S3; list them with download links.
 */
import { Box, Button, CircularProgress, Link, Popover, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { GraphicalAsset, GraphicalAssetsDetail, PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

export function GraphicalAssetsControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const detail = item.detail as unknown as GraphicalAssetsDetail;
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ anchorEl: HTMLElement; asset: GraphicalAsset } | null>(null);
  const assets: GraphicalAsset[] = Array.isArray(detail?.assets) ? detail.assets : [];

  const generate = useMutation({
    mutationFn: () => items.generateGraphicalAssets(topId, item.id, file as File),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Graphical assets generated.', 'success');
        setFile(null);
        onChanged();
      } else {
        snackbar.show(typeof res.detail === 'string' ? res.detail : 'Could not generate assets.', 'error');
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not generate assets.', 'error'),
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {assets.length > 0 ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Generated assets:
            </Typography>
            <Stack spacing={0.5}>
              {assets.map((a) => (
                <Stack
                  key={a.name}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  onMouseEnter={(e) => a.url && setPreview({ anchorEl: e.currentTarget, asset: a })}
                  onMouseLeave={() => setPreview(null)}
                >
                  <Typography variant="body2">
                    {a.name} ({a.width}×{a.height})
                  </Typography>
                  {a.url && (
                    <Link href={a.url} target="_blank" rel="noopener" variant="body2">
                      download
                    </Link>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No assets generated yet.
          </Typography>
        )}

        {canManage && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" component="label" disabled={generate.isPending}>
              {file ? file.name : 'Choose source image'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => generate.mutate()}
              disabled={!file || generate.isPending}
              startIcon={generate.isPending ? <CircularProgress size={14} /> : undefined}
            >
              Generate assets
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Hover preview shown in a popover above the asset row / download link. */}
      <Popover
        open={Boolean(preview)}
        anchorEl={preview?.anchorEl ?? null}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setPreview(null)}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
      >
        {preview?.asset.url && (
          <Box
            component="img"
            src={preview.asset.url}
            alt={preview.asset.name}
            sx={{ display: 'block', maxWidth: 260, maxHeight: 260, m: 1 }}
          />
        )}
      </Popover>
    </Box>
  );
}
